import React, { useState, useCallback } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { useSelector } from 'react-redux';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import BadgeIcon from '@mui/icons-material/Badge';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SecurityIcon from '@mui/icons-material/Security';
import uploadIcon from 'src/Assets/Icons/upload.svg';
import Functions from 'src/Helpers/Functions';
import ImageRepository from 'src/Repositories/ImageRepository';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import { userTeachrSetAccountAction } from 'src/Redux/Actions/UserActions';
import { createImageRef, createTeachrAddress, prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import ButtonField from 'src/Components/Ui/ButtonField';
import Settings from 'src/Components/Settings';
import { ENV_VERIFIED_TRAINER, isBrandActive } from 'src/config/environment.config';

const fileTypes = ['PDF', 'PNG', 'JPEG', 'JPG'];

interface UploadZoneProps {
    onFileChange: (file: File) => void;
    onTypeError: () => void;
    name: string;
    error: boolean;
    classes: any;
}

const UploadZone: React.FC<UploadZoneProps> = ({ onFileChange, onTypeError, name, error, classes }) => (
    <FileUploader
        classes="focus-within:bg-blue-50"
        hoverTitle="Déposez votre fichier ici"
        onTypeError={onTypeError}
        handleChange={onFileChange}
        name={name}
        types={fileTypes}
    >
        <div className="flex flex-col lg:flex-row items-center justify-center gap-4 py-6">
            <img src={uploadIcon} alt="Upload" className="w-12 h-12 flex-shrink-0" />
            <div className="flex flex-col items-center lg:items-start gap-1">
                <p className={`text-sm font-semibold text-center lg:text-left ${error ? classes.error : classes.text} ${classes.fontFamily}`}>
                    Faites glisser votre fichier ou choisissez{' '}
                    <span className={`font-bold ${error ? classes.error : classes.primaryText} cursor-pointer hover:underline`}>
                        un fichier
                    </span>
                </p>
                <p className={`text-xs ${error ? classes.error : classes.text}`}>
                    Formats acceptés : PDF, PNG, JPEG, JPG
                </p>
            </div>
        </div>
    </FileUploader>
);

interface FileCardProps {
    fileName: string;
    onRemove: () => void;
    classes: any;
}

const FileCard: React.FC<FileCardProps> = ({ fileName, onRemove, classes }) => (
    <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
            <DescriptionIcon className={`w-7 h-7 ${classes.primaryText}`} />
            <span className={`font-semibold text-sm ${classes.primaryText} ${classes.fontFamily}`}>
                {fileName}
            </span>
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
        </div>
        <button
            onClick={onRemove}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
            <DeleteIcon className="w-4 h-4" />
            Supprimer
        </button>
    </div>
);

interface DocumentSectionProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    required: boolean;
    file: File | null;
    existingUrl?: string;
    existingLabel?: string;
    error: boolean;
    uploadName: string;
    onFileChange: (file: File) => void;
    onRemove: () => void;
    onTypeError: () => void;
    classes: any;
    common: any;
    primaryHex: string;
}

const DocumentSection: React.FC<DocumentSectionProps> = ({
    icon, title, description, required,
    file, existingUrl, existingLabel, error,
    uploadName, onFileChange, onRemove, onTypeError,
    classes, common, primaryHex,
}) => (
    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 space-y-3">
        <div className="flex items-start gap-3">
            <div
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${primaryHex}1a` }}
            >
                {icon}
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <p className={`font-bold text-gray-900 ${classes.fontFamily}`}>{title}</p>
                    {required
                        ? <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Requis</span>
                        : <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Facultatif</span>
                    }
                </div>
                <p className={`text-xs text-gray-500 mt-0.5 ${classes.fontFamily}`}>{description}</p>
            </div>
        </div>

        {file ? (
            <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg px-3 py-2">
                <FileCard fileName={file.name} onRemove={onRemove} classes={classes} />
            </div>
        ) : existingUrl ? (
            <div className="space-y-2">
                <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-3 flex items-center gap-3">
                    <DescriptionIcon className={`w-7 h-7 ${classes.primaryText}`} />
                    <a
                        href={existingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-semibold text-sm ${classes.primaryText} hover:underline ${classes.fontFamily}`}
                    >
                        {existingLabel}
                    </a>
                    <CheckCircleIcon className="w-5 h-5 text-green-600 ml-auto" />
                </div>
                <p className={`text-xs text-center text-gray-500 ${common.body}`}>Ou remplacer par un nouveau fichier</p>
                <div className={`bg-gray-50 border-2 border-dashed rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all ${error ? 'border-red-300' : 'border-gray-300'}`}>
                    <UploadZone onFileChange={onFileChange} onTypeError={onTypeError} name={uploadName} error={error} classes={classes} />
                </div>
            </div>
        ) : (
            <div className={`border-2 border-dashed rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all ${error ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300'}`}>
                <UploadZone onFileChange={onFileChange} onTypeError={onTypeError} name={uploadName} error={error} classes={classes} />
            </div>
        )}
    </div>
);

const CooperativeDocumentUploadStep: React.FC = () => {
    const { teachr, nextStep, dispatch } = useProfilContext();
    const { classes, common, brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    // const formData = useSelector((state: any) => state.user?.cooperativeFormData); 

    const [identityFile, setIdentityFile] = useState<File | null>(null);
    const [kbisFile, setKbisFile] = useState<File | null>(null);
    const [insuranceFile, setInsuranceFile] = useState<File | null>(null);

    const [identityError, setIdentityError] = useState(false);
    const [kbisError, setKbisError] = useState(false);
    const [insuranceError, setInsuranceError] = useState(false);

    const [isSportDomain, setIsSportDomain] = useState<boolean | null>(isBrandActive(ENV_VERIFIED_TRAINER) ? true : null);
    const [sportDomainError, setSportDomainError] = useState(false);

    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);

    const handleSportDomainChange = (_: any, value: boolean | null) => {
        if (value === null) return;
        setIsSportDomain(value);
        setSportDomainError(false);
    };

    const handleFileChange = useCallback((
        file: File,
        setter: React.Dispatch<React.SetStateAction<File | null>>,
        errorSetter: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (!file) return;
        errorSetter(false);
        if (file.type.startsWith('image/')) {
            const ext = file.type.split('/')[1];
            Functions.resizeFile(file, true, ext).then((resized: any) => {
                setter(resized);
            }).catch(() => {
                setter(file);
            });
        } else {
            setter(file);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        const hasIdentity = identityFile || teachr?.proofIdentity;
        const hasKbis = kbisFile || teachr?.proofSignSap;
        const hasInsurance = insuranceFile || teachr?.proofIdentity2;
        if (!hasIdentity) { setIdentityError(true); return; }
        if (!hasKbis) { setKbisError(true); return; }
        if (isSportDomain === null) { setSportDomainError(true); return; }
        if (!hasInsurance && isSportDomain === true) { setInsuranceError(true); return; }

        setLoading(true);
        setGlobalError(null);

        try {
            const updatedTeachr = { ...teachr } as any;

            if (identityFile) {
                updatedTeachr.proofIdentity = await createImageRef(identityFile, 'proof-identity-coop');
            }
            if (kbisFile) {
                updatedTeachr.proofSignSap = await createImageRef(kbisFile, 'proof-kbis-coop');
            }
            if (insuranceFile) {
                updatedTeachr.proofIdentity2 = await createImageRef(insuranceFile, 'proof-insurance-coop');
            }

            // const addressIri = (formData?.address && teachr.id && teachr['@id'])
            // ? await createTeachrAddress(formData.address, teachr['@id'] as string, teachr.id)
            //     : null;

            // if (addressIri) updatedTeachr.address = addressIri;
            updatedTeachr.scoop = '20';
            prepareTeachrForUpdate(updatedTeachr, {});
            await updateTeachrData(updatedTeachr, dispatch, setLoading);
            nextStep(4);
        } catch (err : any) {
            console.warn('Erreur lors de l\'upload des documents:', err);
            const desc = err?._description ?? '';
            if (desc.includes("exceeds your upload_max_filesize ini directive (limit is 2048 KiB)")) {
                setGlobalError("⚠️ Votre fichier est trop volumineux,\n Le fichier que vous tentez d'envoyer dépasse la taille maximale autorisée de 2 Mo.\n Pour finaliser votre inscription, merci de compresser ou réduire la taille de votre document avant de le renvoyer.");
            } else if (desc.includes("The file size must not exceed 5 MB") || desc.includes("The file size must not exceed 5 mbits")) {
                setGlobalError("⚠️ Votre fichier est trop volumineux.\n La taille maximale autorisée est de 5 Mo. Veuillez compresser ou réduire la taille de votre document avant de le renvoyer.");
            } else if (desc.includes("Invalid or unsupported image format")) {
                setGlobalError("⚠️ Format de fichier invalide.\n Veuillez utiliser un fichier au format JPEG, PNG, GIF ou WebP. Les fichiers PDF ne sont pas acceptés pour les images.");
            } else if (desc.includes('"file" is required')) {
                setGlobalError("⚠️ Aucun fichier sélectionné.\n Veuillez sélectionner un document avant de soumettre.");
            } else {
                setGlobalError('Une erreur est survenue lors de l\'envoi des documents. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    }, [identityFile, kbisFile, insuranceFile, isSportDomain, teachr, dispatch, nextStep]);

    return (
        <Settings
            title="Documents requis"
            subtitle="Fournissez vos justificatifs pour finaliser votre adhésion"
            height="100%"
        >
            <Grid item xs={12}>
                <div className="space-y-5 w-full">
                    {/* Info banner */}
                    <div className="p-3 rounded-lg border flex items-start gap-2" style={{ backgroundColor: `${primaryHex}0d`, borderColor: `${primaryHex}40` }}>
                        <SecurityIcon style={{ color: primaryHex, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
                        <p className={`text-sm text-gray-700 ${classes.fontFamily}`}>
                            Ces documents sont nécessaires pour valider votre adhésion à la coopérative. Vos fichiers sont transmis de manière sécurisée.
                        </p>
                    </div>

                    {/* Pièce d'identité */}
                    <DocumentSection
                        icon={<BadgeIcon style={{ color: primaryHex, fontSize: 20 }} />}
                        title="Pièce d'identité"
                        description="Carte d'identité, passeport ou titre de séjour recto et verso fusionnés sur un seul document"
                        required
                        file={identityFile}
                        existingUrl={teachr?.proofIdentity?.contentUrl}
                        existingLabel={`Identité ${teachr?.firstName?.charAt(0) ?? ''}${teachr?.lastName?.charAt(0) ?? ''}-${teachr?.id}`}
                        error={identityError}
                        uploadName="identityFile"
                        onFileChange={(f) => handleFileChange(f, setIdentityFile, setIdentityError)}
                        onRemove={() => { setIdentityFile(null); setIdentityError(false); }}
                        onTypeError={() => setIdentityError(true)}
                        classes={classes}
                        common={common}
                        primaryHex={primaryHex}
                    />

                    {/* KBIS */}
                    <DocumentSection
                        icon={<BusinessCenterIcon style={{ color: primaryHex, fontSize: 20 }} />}
                        title="Extrait KBIS"
                        description="Extrait d'immatriculation au registre du commerce et des sociétés (moins de 3 mois)"
                        required
                        file={kbisFile}
                        existingUrl={teachr?.proofSignSap?.contentUrl}
                        existingLabel={`KBIS ${teachr?.firstName?.charAt(0) ?? ''}${teachr?.lastName?.charAt(0) ?? ''}-${teachr?.id}`}
                        error={kbisError}
                        uploadName="kbisFile"
                        onFileChange={(f) => handleFileChange(f, setKbisFile, setKbisError)}
                        onRemove={() => { setKbisFile(null); setKbisError(false); }}
                        onTypeError={() => setKbisError(true)}
                        classes={classes}
                        common={common}
                        primaryHex={primaryHex}
                    />

                    {/* Question : domaine sportif ? */}
                    <div
                        className={`p-4 border rounded-xl space-y-3 ${isBrandActive(ENV_VERIFIED_TRAINER) ? 'hidden' : 'bg-teachr/10'}`}
                        style={{
                            borderColor: sportDomainError ? '#fca5a5' : '#e5e7eb',
                            backgroundColor: sportDomainError ? '#fff5f5' : '#f9fafb',
                        }}
                    >
                        <div className="flex items-start gap-3">
                            <div
                                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: `${primaryHex}1a` }}
                            >
                                <FitnessCenterIcon style={{ color: primaryHex, fontSize: 20 }} />
                            </div>
                            <div>
                                <p className={`font-bold text-gray-900 ${classes.fontFamily}`}>
                                    Votre activité relève-t-elle du domaine sportif ?
                                </p>
                                <p className={`text-xs text-gray-500 mt-0.5 ${classes.fontFamily}`}>
                                    Si oui, une attestation d'assurance responsabilité civile professionnelle sera demandée.
                                </p>
                            </div>
                        </div>

                        <ToggleButtonGroup
                            value={isSportDomain}
                            exclusive
                            onChange={handleSportDomainChange}
                            size="small"
                            sx={{ gap: 1, ml: '48px' }}
                        >
                            <ToggleButton
                                value={true}
                                sx={{
                                    px: 3,
                                    fontFamily: brandConfig.fontFamily,
                                    fontWeight: 600,
                                    borderRadius: '8px !important',
                                    border: `1px solid ${sportDomainError ? '#dc2626' : '#d1d5db'} !important`,
                                    '&.Mui-selected': {
                                        bgcolor: `${primaryHex}15`,
                                        borderColor: `${primaryHex} !important`,
                                        color: primaryHex,
                                    },
                                }}
                            >
                                Oui
                            </ToggleButton>
                            <ToggleButton
                                value={false}
                                sx={{
                                    px: 3,
                                    fontFamily: brandConfig.fontFamily,
                                    fontWeight: 600,
                                    borderRadius: '8px !important',
                                    border: `1px solid ${sportDomainError ? '#dc2626' : '#d1d5db'} !important`,
                                    '&.Mui-selected': {
                                        bgcolor: '#f0fdf4',
                                        borderColor: '#16a34a !important',
                                        color: '#16a34a',
                                    },
                                }}
                            >
                                Non
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {sportDomainError && (
                            <p className={`text-xs text-red-600 italic font-semibold ml-12 ${classes.fontFamily}`}>
                                Veuillez répondre à cette question avant de continuer.
                            </p>
                        )}
                    </div>

                    {/* Attestation d'assurance - uniquement si domaine sportif */}
                    {(isSportDomain === true || isBrandActive(ENV_VERIFIED_TRAINER)) && (
                        <DocumentSection
                            icon={<SecurityIcon style={{ color: primaryHex, fontSize: 20 }} />}
                            title="Attestation d'assurance en cours"
                            description="Attestation d'assurance responsabilité civile professionnelle en cours de validité"
                            required={true}
                            file={insuranceFile}
                            existingUrl={teachr?.proofIdentity2?.contentUrl}
                            existingLabel={`Assurance ${teachr?.firstName?.charAt(0) ?? ''}${teachr?.lastName?.charAt(0) ?? ''}-${teachr?.id}`}
                            error={insuranceError}
                            uploadName="insuranceFile"
                            onFileChange={(f) => handleFileChange(f, setInsuranceFile, setInsuranceError)}
                            onRemove={() => { setInsuranceFile(null); setInsuranceError(false); }}
                            onTypeError={() => setInsuranceError(true)}
                            classes={classes}
                            common={common}
                            primaryHex={primaryHex}
                        />
                    )}

                    {globalError && (
                        <p className={`text-sm text-red-600 text-center ${classes.fontFamily}`}>{globalError}</p>
                    )}

                    <div className="flex lg:flex-row flex-col gap-y-2 justify-between pt-2">
                        <ButtonField
                            label="Précédent"
                            variant="outlined"
                            className="w-full font-bold lg:w-auto"
                            onClick={() => nextStep(2)}
                            disabled={loading}
                        />
                        <ButtonField
                            label="Suivant"
                            variant="contained"
                            className="w-full font-bold lg:w-auto"
                            onClick={handleSubmit}
                            disabled={loading}
                        />
                    </div>
                </div>
            </Grid>
        </Settings>
    );
};

export default CooperativeDocumentUploadStep;
